import {exam_getExaminationInfoList_Api, exam_getExaminations_Api} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {exam_getExaminationInfos_Api} from '../../../apis'

const FilterSelectField = ({
  name,
  value,
  onChange,
  options,
  isLoading,
  className,
  ...props
}) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, { value }) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator />
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const ExaminationsAsyncWrapper = compose(
  branch(
    ({ owner_id, owner_type }) => _.some([owner_id, owner_type], _.isEmpty),
    renderNothing
  )
)(({ owner_id, owner_type, children }) => {
  const [values, setValues] = useState()

  const apiInfo = exam_getExaminations_Api

  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
      JSON.stringify(values),
    ].join('/'),
    apiInfo,
    values,
    deps: [values],
    query: bindQueryParams([{ id: owner_id }, { type: owner_type }]),
  })

  return _.isFunction(children)
    ? children({
        isLoading,
        items: getResponseItems(response),
        onSearch: (keyword) => {
          setValues({
            keyword,
          })
        },
      })
    : children
})

const ExaminationsInfoAsyncWrapper = compose(
  branch(({ examination_id }) => _.isEmpty(examination_id), renderNothing)
)(({ examination_id, children }) => {
  const [values, setValues] = useState()

  const apiInfo = exam_getExaminationInfoList_Api

  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', examination_id),
      JSON.stringify(values),
    ].join('/'),
    apiInfo,
    values,
    deps: [values],
    query: bindQueryParam({ id: examination_id }),
  })

  return _.isFunction(children)
    ? children({
        isLoading,
        items: getResponseItems(response),
        onSearch: (keyword) => {
          setValues({
            keyword,
          })
        },
      })
    : children
})

export const ExaminationReportGroupByInfosFilter = ({
  owner_id,
  owner_type,
  defaultValue,
  value: filter = {
    examination_id: undefined,
    examination_info_id: undefined,
    filter_examination_info_id: undefined,
    examination_info_answer_id: undefined,
  },
  onChange: setValues = NullAsDefault,
}) => {
  const t = useTranslate()

  const apiInfo = exam_getExaminationInfos_Api
  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', filter.examination_id),
      JSON.stringify({}),
    ].join('/'),
    apiInfo,
    values: {},
    // deps: [{}],
    query: bindQueryParam({ id: filter.examination_id }),
  })

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
      <ExaminationsAsyncWrapper
        {...{
          owner_id,
          owner_type,
        }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, ...item }) => ({
            value: id,
            label: t(getTitle(item)),
          }))
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('choose examination')} : `}
              </span>
              <FilterSelectField
                name="examination_id"
                loading={isLoading}
                value={filter.examination_id}
                placeholder={t(isLoading ? 'loading...' : 'examination')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsAsyncWrapper>

      <ExaminationsInfoAsyncWrapper
        {...{ examination_id: filter.examination_id }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, label }) => ({
            value: id,
            label: t(label).replace('?', ''),
          }))
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('filter by field')} : `}
              </span>
              <FilterSelectField
                name="filter_examination_info_id"
                loading={isLoading}
                value={filter.filter_examination_info_id}
                placeholder={t(isLoading ? 'loading...' : 'click to select')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsInfoAsyncWrapper>

      {_.get(response, 'data.data', [])
        .filter((e) => e.id === filter.filter_examination_info_id)
        .map((e, i) => {
          const options = _.get(e, 'examination_info_answers', []).map(
            ({ id, title }) => ({
              value: id,
              label: t(title).replace('?', ''),
            })
          )

          return (
            <div
              key={i}
              className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${e.title.replace('?', '')} : `}
              </span>
              <FilterSelectField
                name="examination_info_answer_id"
                loading={isLoading}
                value={filter.examination_info_answer_id}
                placeholder={t(isLoading ? 'loading...' : 'all')}
                mode={e.answer_type === 'multiple' ? 'multiple' : 'single'}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        })}

      <ExaminationsInfoAsyncWrapper
        {...{ examination_id: filter.examination_id }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, label }) => ({
            value: id,
            label: t(label).replace('?', ''),
          }))
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('display by field')} : `}
              </span>
              <FilterSelectField
                name="examination_info_id"
                loading={isLoading}
                value={filter.examination_info_id}
                placeholder={t(isLoading ? 'loading...' : 'default')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsInfoAsyncWrapper>
    </div>
  )
}

export default ExaminationReportGroupByInfosFilter
